import { Injectable, inject } from '@angular/core';
import { BdUtilScriptService } from '@bindoc-projects/util-lib/data';
import { BdWindowRefService } from '@bindoc-projects/platform-lib/data';
import { BdCurrentUserService } from '../current-user/current-user.service';
import { AccountInfo } from '@azure/msal-node';
import { delay, map, Observable, Subject } from 'rxjs';

/**
 * Where to send a user whose browser blocked the Zendesk widget (Edge tracking
 * prevention is the common cause), so every entry point into support can offer
 * the same fallback instead of silently doing nothing.
 */
export const BD_ZENDESK_BLOCKED_HELP_URL =
  'https://bindoc.atlassian.net/wiki/spaces/HP/pages/174030849/Informationen+f+r+System+Administratoren#Edge-Browser-%E2%80%93-Nachverfolgungspr%C3%A4vention-blockiert-Support-Integration';

/**
 * Context a caller can attach to the ticket it is about to open, so the user does
 * not have to transcribe technical detail by hand (and support does not have to
 * ask for it).
 */
export interface BdZendeskTicketContext {
  /** Prefilled into the contact form's description field. */
  description?: string;
}

@Injectable({
  providedIn: 'root',
})
export class BdUserZendeskService {
  private readonly _scriptService = inject(BdUtilScriptService);
  private readonly _windowService = inject(BdWindowRefService);
  private readonly _currentUserService = inject(BdCurrentUserService);

  private readonly _window: Window;
  private readonly _ticketSubmitted = new Subject<void>();

  public readonly ticketSubmitted$ = this._ticketSubmitted.asObservable();

  private _currentUser: AccountInfo | null = null;
  private _listenersRegistered = false;

  constructor() {
    this._window = this._windowService.get();
    this._currentUserService.currentUser.subscribe((currentUser) => {
      this._currentUser = currentUser;
    });
  }

  public init(): Observable<void> {
    return this._scriptService
      .loadJsScript({
        id: 'ze-snippet',
        async: true,
        src: 'https://static.zdassets.com/ekr/snippet.js?key=ae01a2ee-3f76-4271-ba66-9e6b7e7b51f0',
        defer: true,
      })
      .pipe(
        delay(2_500), // wait for the script to be executed
        map(() => {
          return this._window['zE']('webWidget', 'hide');
        }),
      );
  }

  public open(context?: BdZendeskTicketContext): void {
    // Must run before `open`: the contact form reads the prefill when it renders.
    this._prefillDescription(context?.description);

    this._window['zE']('webWidget', 'show');
    this._window['zE']('webWidget', 'open');

    this._prefillBinDocEmployee();
    this._registerListenersOnce();
  }

  /**
   * Prefills the contact form's description. The widget has no `prefill` command
   * for it (that one only covers name/email/phone), so it goes through
   * `updateSettings` — `'*'` applies the value to every locale variant of the
   * form.
   */
  private _prefillDescription(description?: string): void {
    if (!description) {
      return;
    }
    this._window['zE']('webWidget', 'updateSettings', {
      webWidget: {
        contactForm: {
          fields: [{ id: 'description', prefill: { '*': description } }],
        },
      },
    });
  }

  /**
   * Registers the persistent `userEvent` and `close` widget listeners exactly
   * once for the lifetime of this root-scoped service. Zendesk invokes *every*
   * registered handler per event, so registering on each `open()` call would
   * accumulate duplicate handlers and emit `ticketSubmitted$` once per prior
   * open — resulting in multiple success toasts for a single submission.
   */
  private _registerListenersOnce(): void {
    if (this._listenersRegistered) {
      return;
    }
    this._listenersRegistered = true;

    this._registerSubmissionListener();

    this._window['zE']('webWidget:on', 'close', () => {
      this._window['zE']('webWidget', 'hide');
    });
  }

  private _prefillBinDocEmployee(): void {
    function isValidBinDocEmail(value: string): boolean {
      const regex = /^[^@]+\.[^@]+@bindoc\.de$/;
      return regex.test(value);
    }

    if (
      this._currentUser &&
      this._currentUser.idTokenClaims.emails.some((email) =>
        isValidBinDocEmail(email),
      )
    ) {
      this._window['zE']('webWidget', 'prefill', {
        name: { value: this._currentUser.name },
        email: { value: this._currentUser.idTokenClaims.emails.at(0) },
      });
    }
  }

  private _registerSubmissionListener(): void {
    this._window['zE'](
      'webWidget:on',
      'userEvent',
      (event: { action: string; category: string }) => {
        if (
          event.category === 'Zendesk Web Widget' &&
          event.action === 'Contact Form Submitted'
        ) {
          this._ticketSubmitted.next();
        }
      },
    );
  }
}
